import { UserType } from 'shared-general-libs/type/user';
import { RoutesConfig } from '@ieApp/routes.config';
import { NavItem } from '@ieShared/interfaces/nav-bar.interface';
import {
  PartnerPermissions,
  Permission,
} from 'shared-general-libs/enum/permission';

export const enum PAGES {
  DASHBOARD = 'DASHBOARD',
  TASKS = 'TASKS',
  ACCOUNTING = 'ACCOUNTING',
  ACCOUNTING_CONSOLIDATED = 'ACCOUNTING_CONSOLIDATED',
  ACCOUNTING_DETAILED = 'ACCOUNTING_DETAILED',
  PLAN = 'PLAN',
  ANALYTICS = 'ANALYTICS',
  MATERIALITY = 'MATERIALITY',
  SET_BASELINE = 'SET_BASELINE',
  SET_GOALS = 'SET_GOALS',
  TRACK_PROGRESS = 'TRACK_PROGRESS',
  GENERATE_PLAN = 'GENERATE_PLAN',
  COLLECT = 'COLLECT',
  COLLECT_COVERAGE = 'COLLECT_COVERAGE',
  REVIEW = 'REVIEW',
  REPORT = 'REPORT',
  WASTE_TRACKING = 'WASTE_TRACKING',
  PRODUCT_TRACKING = 'PRODUCT_TRACKING',
  PARTNER_TRACKING = 'PARTNER_TRACKING',
  PARTNER_TRACKING_DETAILS = 'PARTNER_TRACKING_DETAILS',
  TRACK = 'TRACK',
  SETTINGS = 'SETTINGS',
  DATA_REQUESTS = 'DATA_REQUESTS',
  WASTE_DATA_REQUESTS = 'WASTE_DATA_REQUESTS',
  APPLICATIONS = 'APPLICATIONS',
  CERTIFICATIONS = 'CERTIFICATIONS',
  AUDITS = 'AUDITS',
  PROJECT = 'PROJECT',
  AUTOMATION = 'AUTOMATION',
}

export function getEnterprisePagePermissions(page: PAGES) {
  return (
    ENTERPRISE_MENU.find((item) => {
      return (
        item.id === page ||
        (item.children && item.children.some((child) => child.id === page))
      );
    })?.permission || []
  );
}

export function getPartnerPagePermissions(page: PAGES) {
  return PARTNER_MENU.find((item) => item.id === page)?.permission || [];
}

const ENTERPRISE_MENU: NavItem[] = [
  {
    id: PAGES.TASKS,
    label: 'NAV_MENU.MY_TASKS',
    icon: 'task',
    route: RoutesConfig.enterpriseMyTasks,
    permission: [Permission.Idr.VIEW_LIST],
    exact: false,
  },
  {
    id: PAGES.ACCOUNTING,
    label: 'NAV_MENU.ACCOUNTING',
    icon: 'activity',
    route: RoutesConfig.enterpriseAccounting,
    permission: [
      Permission.Accounting.VIEW,
      Permission.Accounting.CONSOLIDATE_VIEW,
    ],
    exact: false,
    children: [
      {
        id: PAGES.ACCOUNTING_DETAILED,
        label: 'NAV_MENU.ACCOUNTING_DETAILED',
        route: RoutesConfig.enterpriseAccounting,
        permission: [Permission.Accounting.VIEW],
        exact: false,
      },
      {
        id: PAGES.ACCOUNTING_CONSOLIDATED,
        label: 'NAV_MENU.ACCOUNTING_CONSOLIDATED',
        route: RoutesConfig.enterpriseAccountingConsolidated,
        permission: [Permission.Accounting.CONSOLIDATE_VIEW],
        exact: false,
      },
    ],
  },
  {
    id: PAGES.ANALYTICS,
    label: 'NAV_MENU.ANALYTICS',
    route: RoutesConfig.enterpriseAnalytics,
    permission: [Permission.Analytics.VIEW],
    icon: 'diagram',
    exact: false,
  },
  {
    id: PAGES.PLAN,
    label: 'NAV_MENU.PLAN',
    icon: 'gps',
    permission: [
      Permission.Survey.VIEW,
      Permission.Plan.VIEW,
      Permission.Baseline.VIEW,
    ],
    exact: false,
    children: [
      {
        id: PAGES.MATERIALITY,
        label: 'NAV_MENU.MATERIALITY',
        route: RoutesConfig.enterpriseMateriality,
        permission: [Permission.Survey.VIEW],
        exact: false,
      },
      {
        id: PAGES.SET_BASELINE,
        label: 'NAV_MENU.SET_BASELINE',
        route: RoutesConfig.enterpriseSetBaseline,
        permission: [Permission.Baseline.VIEW],
        exact: false,
      },
      {
        id: PAGES.SET_GOALS,
        label: 'NAV_MENU.SET_GOALS',
        route: RoutesConfig.enterpriseSetGoals,
        permission: [Permission.Goal.VIEW],
        exact: false,
      },
      // {
      //   id: PAGES.TRACK_PROGRESS,
      //   label: 'NAV_MENU.TRACK_PROGRESS',
      //   route: RoutesConfig.enterpriseTrackProgress,
      //   permission: [Permission.Baseline.VIEW],
      //   exact: false,
      // },
      {
        id: PAGES.GENERATE_PLAN,
        label: 'NAV_MENU.GENERATE_PLAN',
        route: RoutesConfig.enterpriseGeneralPlan,
        permission: [Permission.Plan.VIEW],
        exact: false,
      },
    ],
  },
  {
    id: PAGES.COLLECT,
    label: 'NAV_MENU.COLLECT',
    icon: '3square',
    route: RoutesConfig.enterpriseCollect,
    permission: [Permission.Collect.VIEW],
    exact: false,
    children: [
      {
        id: PAGES.COLLECT_COVERAGE,
        label: 'NAV_MENU.COVERAGE',
        route: RoutesConfig.enterpriseCollectCoveragePicker,
        permission: [Permission.Collect.VIEW],
        exact: false,
      },
    ],
  },
  {
    id: PAGES.REVIEW,
    label: 'NAV_MENU.REVIEW',
    icon: 'review',
    route: RoutesConfig.enterpriseReview,
    permission: [Permission.Idr.VIEW_REVIEW, Permission.Goal.APPROVER],
    exact: false,
  },
  {
    id: PAGES.REPORT,
    label: 'NAV_MENU.REPORT',
    icon: 'report',
    route: RoutesConfig.enterpriseReport,
    permission: [Permission.Report.VIEW],
    exact: false,
  },
  {
    id: PAGES.TRACK,
    label: 'NAV_MENU.TRACK',
    icon: 'favorite-chart',
    permission: [Permission.WasteMaterial.VIEW, Permission.Product.VIEW],
    children: [
      {
        id: PAGES.WASTE_TRACKING,
        label: 'NAV_MENU.WASTE',
        route: RoutesConfig.enterpriseWasteTracking,
        permission: [Permission.WasteMaterial.VIEW],
        exact: false,
      },
      {
        id: PAGES.PRODUCT_TRACKING,
        label: 'NAV_MENU.PRODUCTS',
        route: RoutesConfig.enterpriseProductTracking,
        permission: [Permission.Product.VIEW],
        exact: false,
      },
      {
        id: PAGES.PARTNER_TRACKING,
        label: 'NAV_MENU.PARTNERS',
        route: RoutesConfig.enterprisePartnersTracking,
        permission: [Permission.Partner.PARTNER_TRACKING],
        exact: false,
      },
    ],
  },
  {
    id: PAGES.PROJECT,
    label: 'NAV_MENU.PROJECTS',
    icon: 'shield',
    route: RoutesConfig.enterpriseProject,
    permission: [Permission.Project.VIEW],
  },
  {
    id: PAGES.SETTINGS,
    label: 'NAV_MENU.SETTINGS',
    icon: 'setting-2',
    route: RoutesConfig.enterpriseSettings,
    permission: [
      ...Object.values(Permission.Organization),
      ...Object.values(Permission.User),
      ...Object.values(Permission.Facility),
      ...Object.values(Permission.Partner),
      ...Object.values(Permission.Equipment),
      ...Object.values(Permission.EmissionFactor),
      ...Object.values(Permission.Role),
      ...Object.values(Permission.Topic),
    ],
    exact: false,
  },
];

const PARTNER_MENU: NavItem[] = [
  {
    id: PAGES.DATA_REQUESTS,
    label: 'NAV_MENU.DATA_REQUESTS',
    icon: 'home',
    route: RoutesConfig.partnerDataRequests,
    permission: [PartnerPermissions.PartnerRequest.VIEW],
    exact: false,
  },
  {
    id: PAGES.WASTE_DATA_REQUESTS,
    label: 'NAV_MENU.WASTE_DATA_REQUESTS',
    icon: 'favorite-chart',
    route: RoutesConfig.partnerWasteDataRequest,
    permission: [Permission.WasteRequest.VIEW],
    exact: false,
  },
  {
    id: PAGES.SETTINGS,
    label: 'NAV_MENU.SETTINGS',
    icon: 'setting-2',
    route: RoutesConfig.partnerSettings,
    permission: [PartnerPermissions.Partner.VIEW],
    exact: false,
  },
];

const PORTFOLIO_USER: NavItem[] = [
  // {
  //   label: 'NAV_MENU.DASHBOARD',
  //   icon: 'home',
  //   route: RoutesConfig.portfolioDashboard,
  //   permission: [],
  //   exact: true,
  // },
  {
    id: PAGES.DATA_REQUESTS,
    label: 'NAV_MENU.DATA_REQUESTS',
    icon: 'task',
    route: RoutesConfig.portfolioDataRequests,
    permission: [],
    exact: true,
  },
];

const CERTIFIER_USER: NavItem[] = [
  // {
  //   label: 'NAV_MENU.DASHBOARD',
  //   icon: 'home',
  //   route: RoutesConfig.certifierDashboard,
  //   permission: [],
  //   exact: true,
  // },
  {
    id: PAGES.APPLICATIONS,
    label: 'NAV_MENU.APPLICATIONS',
    icon: 'home',
    permission: [],
    opened: false,
    children: [
      {
        label: 'NAV_MENU.ASSIGN',
        route: RoutesConfig.certifierAssign,
        permission: [],
        exact: false,
      },
      {
        label: 'NAV_MENU.VERIFY',
        route: RoutesConfig.certifierVerify,
        permission: [],
        exact: false,
      },
      {
        label: 'NAV_MENU.REVIEW',
        route: RoutesConfig.certifierReview,
        permission: [],
        exact: false,
      },
    ],
  },
  {
    id: PAGES.CERTIFICATIONS,
    label: 'NAV_MENU.CERTIFICATIONS',
    icon: 'home',
    route: RoutesConfig.certifierCertifications,
    permission: [],
    exact: true,
  },
  {
    id: PAGES.AUDITS,
    label: 'NAV_MENU.AUDITS',
    icon: 'medal-star',
    route: RoutesConfig.certifierAudits,
    permission: [],
    exact: true,
  },
];

const AUDITOR_USER: NavItem[] = [
  // {
  //   label: 'NAV_MENU.DASHBOARD',
  //   icon: 'home',
  //   route: RoutesConfig.auditorDashboard,
  //   permission: [],
  //   exact: true,
  // },
  {
    id: PAGES.APPLICATIONS,
    label: 'NAV_MENU.APPLICATIONS',
    icon: 'home',
    opened: false,
    permission: [],
    children: [
      {
        label: 'NAV_MENU.ASSIGN',
        route: RoutesConfig.auditorAssign,
        permission: [],
        exact: false,
      },
      {
        label: 'NAV_MENU.VERIFY',
        route: RoutesConfig.auditorVerify,
        permission: [],
        exact: false,
      },
      {
        label: 'NAV_MENU.REVIEW',
        route: RoutesConfig.auditorReview,
        permission: [],
        exact: false,
      },
    ],
  },
  {
    id: PAGES.CERTIFICATIONS,
    label: 'NAV_MENU.CERTIFICATIONS',
    icon: 'home',
    route: RoutesConfig.auditorCertifications,
    permission: [],
    exact: true,
  },
  {
    id: PAGES.AUDITS,
    label: 'NAV_MENU.AUDITS',
    icon: 'medal-star',
    route: RoutesConfig.auditorAudits,
    permission: [],
    exact: true,
  },
];

const SUPER_ADMIN: NavItem[] = [
  {
    id: PAGES.SETTINGS,
    label: 'NAV_MENU.SETTINGS',
    icon: 'setting-2',
    route: RoutesConfig.adminSettings,
    permission: [],
    exact: false,
  },
];

export const NAV_MENU = {
  [UserType.ENTERPRISE_USER]: ENTERPRISE_MENU,
  [UserType.ENTERPRISE_ADMIN]: ENTERPRISE_MENU,
  [UserType.PARTNER_USER]: PARTNER_MENU,
  [UserType.PORTFOLIO_USER]: PORTFOLIO_USER,
  [UserType.CERTIFIER_USER]: CERTIFIER_USER,
  [UserType.AUDITOR_USER]: AUDITOR_USER,
  [UserType.SUPER_ADMIN]: SUPER_ADMIN,
};
